import React, { memo, useCallback, useState } from "react";
import BasicModal from "../../../modals/BasicModal";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import InputWrapper from "../../../inputs/shared/InputWrapper";
import LoanTagsRenderer from "./LoanTagsRenderer";
import TextInput from "../../../inputs/TextInput";
import {
  Add,
  AddRounded,
  CloseRounded,
  LocalOfferOutlined,
} from "@mui/icons-material";
import { isEmpty, isFunction, isNil } from "lodash";
import Txt from "../../../typography/Txt";
import Flx from "../../../layout/Flx";
import { blue, grey } from "@mui/material/colors";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { useSelector } from "react-redux";

const LoanTagsModal = memo(({ show, onClose, onSuccessFn }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const [newTags, setNewTags] = useState([]);
  const [currentInput, setCurrentInput] = useState("");

  const { addLoanTag, loading } = useUnderwritingHook();

  const NewTagItem = ({ children, onDelete }) => {
    return (
      <Chip
        icon={<LocalOfferOutlined sx={{ fontSize: "11px" }} />}
        deleteIcon={
          <Tooltip title="Remove Tag">
            <CloseRounded
              sx={{
                ml: "4px !important",
                fontSize: "15px !important",
                color: `${grey[900]} !important`,
              }}
              className="thin"
            />
          </Tooltip>
        }
        label={children}
        onDelete={onDelete}
        sx={{
          height: "24px",
          px: 0.3,
          background: blue[50],
          border: `1px dashed ${blue[300]}`,
        }}
      />
    );
  };

  const onNewTagDelete = useCallback((tagToRemove) => {
    setNewTags((prev) => prev.filter((tag) => tag !== tagToRemove));
  }, []);

  const onAddToNewTags = useCallback(() => {
    if (currentInput.trim() && !newTags.includes(currentInput.trim())) {
      setNewTags((prev) => [...prev, currentInput.trim()]);
      setCurrentInput("");
    }
  }, [currentInput, newTags]);

  const onKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onAddToNewTags();
      }
    },
    [onAddToNewTags]
  );

  const onTagsAdd = useCallback(async () => {
    console.log("newTags", newTags);
    console.log("currentInput", currentInput);
    // return;
    const tagsList = isEmpty(newTags) ? [currentInput] : newTags;
    // console.log("tagsList", tagsList);
    // return;
    addLoanTag({
      dealId: loanDrilldown?._id,
      tags: tagsList,
      onSuccessFn: (data) => {
        setNewTags([]);
        setCurrentInput("");
        if (isFunction(onSuccessFn)) {
          onSuccessFn(data);
          handleOnClose();
        }
      },
    });
  }, [loanDrilldown?._id, newTags, addLoanTag]);

  const handleOnClose = useCallback(() => {
    onClose(false);
    setNewTags([]);
    setCurrentInput("");
  }, []);

  return (
    <BasicModal
      show={show}
      onClose={handleOnClose}
      title="Add Loan Tags"
      maxWidth="sm"
    >
      <Box sx={{ mb: 2 }}>
        <InputWrapper label="Existing Loan Tags">
          <LoanTagsRenderer
            sx={{ mt: 1 }}
            allowDelete
            onDeleteSuccess={onSuccessFn}
          />
        </InputWrapper>
        <Divider sx={{ my: 4 }} />
        <InputWrapper label="Add Additional Tags">
          <TextInput
            sx={{
              ".MuiFormHelperText-root": {
                fontSize: "12px",
                fontStyle: "italic",
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalOfferOutlined sx={{ opacity: 0.4 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Add to list">
                      <Box>
                        <IconButton
                          size="small"
                          onClick={onAddToNewTags}
                          disabled={
                            !currentInput.trim() ||
                            newTags.includes(currentInput.trim())
                          }
                          color="primary"
                          // sx={{ color: blue[600] }}
                        >
                          <Add sx={{ fontSize: "16px" }} />
                        </IconButton>
                      </Box>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
            value={currentInput}
            onChange={setCurrentInput}
            onKeyPress={onKeyPress}
            placeholder="Type tag name and press Enter or click +"
            helperText="Press Enter or click + to add each tag multiple tags"
          />
        </InputWrapper>
      </Box>

      {newTags.length > 0 && (
        <Box sx={{ mb: 2, mt: 3 }}>
          <Txt variant="body2" sx={{ mb: 1, color: grey[600] }}>
            Tags to add:
          </Txt>
          <Flx wrap gap={0.5} sx={{ mt: 1 }}>
            {newTags.map((tag) => (
              <NewTagItem onDelete={() => onNewTagDelete(tag)} key={tag}>
                {tag}
              </NewTagItem>
            ))}
          </Flx>
        </Box>
      )}

      <Flx fw end sx={{ mt: 2 }}>
        <Button
          onClick={onTagsAdd}
          loading={loading}
          disabled={newTags.length === 0 && currentInput === ""}
          startIcon={<AddRounded />}
        >
          Add {newTags.length > 1 ? `(${newTags.length}) Tags` : "Tag"}
        </Button>
      </Flx>
    </BasicModal>
  );
});

export default LoanTagsModal;
